import { useMemo, useState } from "react";
import { DayHeaderCard } from "./components/DayHeaderCard";
import { DayTabs } from "./components/DayTabs";
import { HeaderBanner } from "./components/HeaderBanner";
import { MonthlyReminderBanner } from "./components/MonthlyReminderBanner";
import { MonthlyView } from "./components/MonthlyView";
import { PillarFilter } from "./components/PillarFilter";
import { PillarView } from "./components/PillarView";
import { SeasonalView } from "./components/SeasonalView";
import { SickDayView } from "./components/SickDayView";
import { TimelineView } from "./components/TimelineView";
import { WeekAtGlance } from "./components/WeekAtGlance";
import { FULL_DAYS, type FullDay } from "./data/days";
import { getMonthlyTasksForToday } from "./data/monthly";
import { PILLAR_CATS } from "./data/pillars";
import { schedule } from "./data/schedule";
import { getDayName, getWeekOfMonth } from "./lib/dateUtils";
import { useChecklistState } from "./lib/useChecklistState";
import { T } from "./styles/theme";
import type { SpecialTab } from "./types";

/** Today's day name in the app's Monday-first week. */
function todayFullDay(): FullDay {
  // JS Sunday=0 → index 6, Monday=1 → 0, etc.
  const dow = new Date().getDay();
  return FULL_DAYS[dow === 0 ? 6 : dow - 1];
}

const WEEK_LABELS = ["1st", "2nd", "3rd", "4th"];

export default function HomeChecklist() {
  const todayName = todayFullDay();
  const now = useMemo(() => new Date(), []);

  // Persistent state (Firestore-backed)
  const {
    checked,
    setChecked,
    checkedMonthly,
    setCheckedMonthly,
    checkedSick,
    setCheckedSick,
    lastDone,
    setLastDone,
    hairLastDone,
    setHairLastDone,
  } = useChecklistState();

  // Local UI-only state (resets on refresh, intentionally)
  const [activeDay, setActiveDay] = useState<FullDay>(todayName);
  const [specialTab, setSpecialTab] = useState<SpecialTab>(null);
  const [pillar, setPillar] = useState<string>("all");
  const [dismissedReminder, setDismissedReminder] = useState(false);
  const [editingDate, setEditingDate] = useState<string | null>(null);
  const [hairSkipped, setHairSkipped] = useState(false);
  const [hairEditOpen, setHairEditOpen] = useState(false);

  // Monthly reminder for today
  const todayWeekOfMonth = getWeekOfMonth(now);
  const todayDayName = getDayName(now);
  const monthlyWeekData = getMonthlyTasksForToday(now);
  const weekLabel = WEEK_LABELS[todayWeekOfMonth - 1] ?? "";
  const reminderTasks = monthlyWeekData
    ? monthlyWeekData.tasks.filter((t) => t.day === `${weekLabel} ${todayDayName}`)
    : [];
  const showReminder =
    reminderTasks.length > 0 && !dismissedReminder && activeDay === todayDayName && !specialTab;

  // Day-level data
  const dayData = schedule[activeDay];
  const filteredTasks =
    pillar === "all"
      ? dayData.tasks
      : dayData.tasks.filter((t) => (PILLAR_CATS[pillar] ?? []).includes(t.cat));

  const total = dayData.tasks.length;
  const done = dayData.tasks.filter((t) => t.id && checked[t.id]).length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const allDone = total > 0 && done === total;

  // Week totals (for the header progress bar)
  const weekTotal = FULL_DAYS.reduce((s, d) => s + schedule[d].tasks.length, 0);
  const weekDone = FULL_DAYS.reduce(
    (s, d) => s + schedule[d].tasks.filter((t) => t.id && checked[t.id]).length,
    0,
  );

  // Handlers
  const toggle = (id: string) =>
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  const switchDay = (day: FullDay) => {
    setActiveDay(day);
    setPillar("all");
    setSpecialTab(null);
  };
  const onSpecialTab = (tab: SpecialTab) => {
    setSpecialTab(tab);
    setPillar("all");
  };

  return (
    <div style={{ minHeight: "100vh", background: T.cream, fontFamily: T.body, color: T.ink }}>
      <HeaderBanner weekDone={weekDone} weekTotal={weekTotal} />

      <DayTabs
        activeDay={activeDay}
        specialTab={specialTab}
        checked={checked}
        onSwitchDay={switchDay}
        onSpecialTab={onSpecialTab}
      />

      <div style={{ padding: "14px 14px 56px", maxWidth: 620, margin: "0 auto" }}>
        {specialTab === "monthly" && (
          <MonthlyView checkedMonthly={checkedMonthly} setCheckedMonthly={setCheckedMonthly} />
        )}

        {specialTab === "seasonal" && (
          <SeasonalView
            lastDone={lastDone}
            setLastDone={setLastDone}
            editingDate={editingDate}
            setEditingDate={setEditingDate}
          />
        )}

        {specialTab === "sickday" && (
          <SickDayView checkedSick={checkedSick} setCheckedSick={setCheckedSick} />
        )}

        {!specialTab && (
          <>
            {showReminder && (
              <MonthlyReminderBanner
                tasks={reminderTasks}
                weekData={monthlyWeekData}
                onDismiss={() => setDismissedReminder(true)}
                onViewMonthly={() => {
                  setSpecialTab("monthly");
                  setDismissedReminder(false);
                }}
              />
            )}

            <DayHeaderCard
              activeDay={activeDay}
              dayData={dayData}
              done={done}
              total={total}
              pct={pct}
              allDone={allDone}
            />

            <PillarFilter pillar={pillar} onChange={setPillar} />

            {pillar === "all" ? (
              <TimelineView
                tasks={filteredTasks}
                checked={checked}
                onToggle={toggle}
                hairLastDone={hairLastDone}
                setHairLastDone={setHairLastDone}
                hairSkipped={hairSkipped}
                setHairSkipped={setHairSkipped}
                hairEditOpen={hairEditOpen}
                setHairEditOpen={setHairEditOpen}
              />
            ) : (
              <PillarView pillar={pillar} tasks={filteredTasks} checked={checked} onToggle={toggle} />
            )}

            <WeekAtGlance activeDay={activeDay} checked={checked} onSwitchDay={switchDay} />
          </>
        )}

        <div
          style={{
            textAlign: "center",
            marginTop: 22,
            fontSize: 11,
            color: T.inkLight,
            fontStyle: "italic",
            letterSpacing: 0.5,
          }}
        >
          Made with love for your family 🤎
        </div>
      </div>
    </div>
  );
}
